// =============================================================================
// A planilha de ferramentas do Financeiro, lida no navegador
//
// "Controle Ferramentas CF" (.xlsx), com as abas da tela de Ferramentas:
// Controle Geral e Cancelados (uma linha por assinatura), Pendências, Novas dos
// CSVs, Painel Financeiro (de onde vem a "Obs / Tendência") e Instruções (de
// onde vêm as "Atenções críticas").
//
// Desde a 066 a leitura guarda a célula COMO ESTÁ: periodicidade, tipo de
// cobrança, dia de vencimento, nome no extrato e usuários ativos são texto, e os
// cinco valores (mensal, trimestral, semestral, anual, total do ano) e o dólar
// vão cada um para a sua coluna. O que o Nexa precisa calcular — o dia da
// cobrança, os termos que casam com o Conta Azul, o número de usuários — sai
// desses textos (`derivarCampos`), e o texto continua sendo o que a tela mostra.
// Célula que não cabe na coluna tipada ("Variado" na coluna de valor) fica em
// `texto_planilha`, e o que merece conferência vira aviso — nunca some, e nunca
// é adivinhado em silêncio.
//
// ⚠️ Login e senha: só são lidos com `comAcesso`, que a tela liga apenas para
// quem pode ver o cofre (066). Saem num array à parte (`acessos`), nunca dentro
// da ferramenta, e a linha de comando nunca os lê.
// =============================================================================

import type { SheetData } from "read-excel-file/browser";
import { paraCentavos } from "./dinheiro.ts";

export type Celula = SheetData[number][number];

export type StatusFerramenta = "ativa" | "verificar" | "nova" | "cancelada";

/** As colunas tipadas que podem ter texto de planilha guardado ao lado. */
export type ColunaComTexto =
  | "valor_mensal_centavos" | "valor_trimestral_centavos" | "valor_semestral_centavos"
  | "valor_anual_centavos" | "valor_total_ano_centavos" | "valor_usd_centavos"
  | "status" | "forma_pagamento" | "renovacao";

export type FerramentaPlanilha = {
  aba: "controle" | "cancelados";
  linha_planilha: number;
  origem_ref: string;
  nome: string;
  tipo: string | null;
  status: StatusFerramenta;
  cancelada_em: string | null;
  moeda: "BRL" | "USD";
  valor_mensal_centavos: string | null;
  valor_trimestral_centavos: string | null;
  valor_semestral_centavos: string | null;
  valor_anual_centavos: string | null;
  valor_total_ano_centavos: string | null;
  valor_usd_centavos: string | null;
  periodicidade: string | null;
  tipo_cobranca: string | null;
  forma_pagamento: "cartao" | "boleto" | "pix" | "transferencia" | "outro" | null;
  cartao_final: string | null;
  dia_vencimento: string | null;
  dia_cobranca: number | null;
  proxima_renovacao: string | null;
  renovacao: "automatica" | "manual" | null;
  /** O que a planilha diz, para a tela casar com o cadastro do Nexa. */
  centro_custo_texto: string;
  conta_texto: string;
  nome_extrato: string | null;
  termos_busca: string[];
  responsavel: string | null;
  usuarios_ativos: string | null;
  qtd_usuarios: number | null;
  uso: string | null;
  url_acesso: string | null;
  observacao: string | null;
  tendencia: string | null;
  texto_planilha: Partial<Record<ColunaComTexto, string>>;
  avisos_importacao: string[];
};

export type AcessoPlanilha = { origem_ref: string; login: string | null; senha: string | null };

export type PendenciaPlanilha = {
  origem_ref: string;
  linha_planilha: number;
  secao: "pendencias" | "atencoes_criticas";
  ferramenta_texto: string;
  urgencia: "critico" | "urgente" | "medio" | "baixo" | null;
  motivo: string | null;
  acao_sugerida: string | null;
  prazo: string | null;
  responsavel: string | null;
  resolvido: boolean;
};

export type CandidataPlanilha = {
  origem_ref: string;
  linha_planilha: number;
  ferramenta_texto: string;
  descritivo_extrato: string | null;
  faturas: string | null;
  valores_observados: string | null;
  dia_cartao: string | null;
  classificacao: string | null;
  status_texto: string | null;
  acao: string | null;
};

/** Sem acento, sem caixa, espaços simples. */
export const normalizar = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

/** "2027-01-20" → "20/01/2027". */
const dataBr = (iso: string) => iso.split("-").reverse().join("/");

const texto = (c: Celula | undefined): string => {
  if (c === null || c === undefined) return "";
  if (c instanceof Date) return dataBr(c.toISOString().slice(0, 10));
  return String(c).trim();
};
const ou = (s: string) => (s ? s : null);

// ── as colunas, pelo título ────────────────────────────────────────────────
// Pelo título e não pela posição: na aba Cancelados a ordem é outra (centro de
// custo antes da conta), e a planilha muda de mão em mão.
const COLUNAS = {
  nome: (t: string) => t.startsWith("ferramenta"),
  tipo: (t: string) => t.startsWith("categoria"),
  moeda: (t: string) => t === "moeda",
  mensal: (t: string) => t.includes("mensal") && t.includes("r$"),
  trimestral: (t: string) => t.includes("trimestral"),
  semestral: (t: string) => t.includes("semestral"),
  anual: (t: string) => t.includes("anual") && t.includes("r$"),
  total: (t: string) => t.startsWith("total"),
  usd: (t: string) => t.includes("usd"),
  forma: (t: string) => t.startsWith("forma de pagamento"),
  periodicidade: (t: string) => t === "periodicidade",
  cobranca: (t: string) => t.startsWith("tipo de cobranca"),
  dia: (t: string) => t.startsWith("dia venc"),
  conta: (t: string) => t.startsWith("conta contabil"),
  cc: (t: string) => t.startsWith("centro de custo"),
  extrato: (t: string) => t.startsWith("nome no cartao"),
  responsavel: (t: string) => t.startsWith("responsavel"),
  usuarios: (t: string) => t.includes("usuarios"),
  status: (t: string) => t === "status",
  uso: (t: string) => t.startsWith("usabilidade"),
  url: (t: string) => t.startsWith("url"),
  renovacao: (t: string) => t.startsWith("renovacao"),
  observacoes: (t: string) => t.startsWith("observac"),
} as const;
type Chave = keyof typeof COLUNAS | "login" | "senha";

/** Login, e-mail de acesso e senha: só lidos com `comAcesso`. */
const SENSIVEL = (t: string) => /senha|login|e-?mail|password/.test(t);

export function mapearColunas(cabecalho: Celula[], comAcesso = false): {
  indice: Partial<Record<Chave, number>>;
  ignoradas: string[];
} {
  const indice: Partial<Record<Chave, number>> = {};
  const ignoradas: string[] = [];
  cabecalho.forEach((c, i) => {
    const t = normalizar(texto(c));
    if (!t) return;
    if (SENSIVEL(t)) {
      if (!comAcesso) { ignoradas.push(texto(c)); return; }
      const k: Chave = /senha|password/.test(t) ? "senha" : "login";
      if (indice[k] === undefined) indice[k] = i;
      return;
    }
    for (const [chave, casa] of Object.entries(COLUNAS) as [keyof typeof COLUNAS, (t: string) => boolean][]) {
      if (indice[chave] === undefined && casa(t)) { indice[chave] = i; break; }
    }
  });
  return { indice, ignoradas };
}

// ── os campos ──────────────────────────────────────────────────────────────

/**
 * Número da célula em centavos. Texto que é um valor ("R$ 1.372,32",
 * "USD 25,03") também vale; o que não é um valor só ("Variado", "~99–999",
 * "$20-30") devolve `texto`, para ficar guardado como está.
 */
export function lerValorCelula(c: Celula | undefined): { centavos: string | null; texto: string | null } {
  if (c === null || c === undefined || c === "") return { centavos: null, texto: null };
  if (typeof c === "number") {
    return Number.isFinite(c) && c > 0 ? { centavos: paraCentavos(c.toFixed(2)), texto: null } : { centavos: null, texto: null };
  }
  const bruto = String(c).trim();
  const limpo = bruto.replace(/^(R\$|US\$|USD|\$)\s*/i, "").trim();
  if (/^[\d.,]+$/.test(limpo)) {
    const v = paraCentavos(limpo);
    if (v !== null && BigInt(v) > 0n) return { centavos: v, texto: null };
  }
  return { centavos: null, texto: bruto };
}

/** Só o número. */
export const lerValor = (c: Celula | undefined) => lerValorCelula(c).centavos;

/** "Cancelado 29/06/26" → "2026-06-29"; "Cancelado Abr/26" → "2026-04-01". */
function dataNoTexto(t: string): string | null {
  const d = t.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (d) {
    const ano = d[3]!.length === 2 ? 2000 + Number(d[3]) : Number(d[3]);
    return `${ano}-${d[2]!.padStart(2, "0")}-${d[1]!.padStart(2, "0")}`;
  }
  const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  const m = normalizar(t).match(/(jan|fev|mar|abr|mai|jun|jul|ago|set|out|nov|dez)[a-z]*\/?\s*(\d{2,4})/);
  if (m) {
    const ano = m[2]!.length === 2 ? 2000 + Number(m[2]) : Number(m[2]);
    return `${ano}-${String(MES.indexOf(m[1]!) + 1).padStart(2, "0")}-01`;
  }
  return null;
}

export function lerStatus(t: string): { status: StatusFerramenta; cancelada_em: string | null; lido: boolean } {
  const n = normalizar(t);
  if (!n) return { status: "verificar", cancelada_em: null, lido: false };
  if (n.includes("cancel") || n.includes("inativ")) return { status: "cancelada", cancelada_em: dataNoTexto(t), lido: true };
  if (n.includes("novo")) return { status: "nova", cancelada_em: null, lido: true };
  if (n.includes("verific") || n.includes("confirmar")) return { status: "verificar", cancelada_em: null, lido: true };
  if (n.startsWith("ativ")) return { status: "ativa", cancelada_em: null, lido: true };
  return { status: "verificar", cancelada_em: null, lido: false };
}

/** "Mensal - Final 3561" → "3561": o final de cartão escrito no fim da periodicidade. */
export function finalDoCartaoNaPeriodicidade(t: string | null): string | null {
  return (t?.match(/final\s*(\d{4})\s*$/i) ?? [])[1] ?? null;
}

export function lerFormaPagamento(t: string): { forma: FerramentaPlanilha["forma_pagamento"]; final: string | null } {
  const n = normalizar(t);
  const final = (t.match(/\b(\d{4})\b/) ?? [])[1] ?? null;
  if (!n) return { forma: null, final: null };
  if (n.includes("cart")) return { forma: "cartao", final };
  if (n.includes("boleto")) return { forma: "boleto", final: null };
  if (n.includes("pix")) return { forma: "pix", final: null };
  if (n.includes("transf") || n.includes("ted")) return { forma: "transferencia", final: null };
  return { forma: "outro", final: null };
}

/**
 * O dia de vencimento da planilha, como texto, e o que dá para tirar dele: o
 * dia da cobrança e, quando há uma data inteira, a data. "Variado", "9 ou 11"
 * e "5–26" não têm um dia só, e ficam só como texto.
 */
export function lerDia(c: Celula | undefined): { texto: string | null; dia: number | null; data: string | null } {
  if (c === null || c === undefined || c === "") return { texto: null, dia: null, data: null };
  if (c instanceof Date) {
    const iso = c.toISOString().slice(0, 10);
    return { texto: dataBr(iso), dia: c.getUTCDate(), data: iso };
  }
  if (typeof c === "number") {
    const ok = Number.isInteger(c) && c >= 1 && c <= 31;
    return { texto: String(c), dia: ok ? c : null, data: null };
  }
  const t = String(c).trim();
  return { texto: t, ...diaDoTexto(t) };
}

/** O dia (e a data) que um texto de dia de vencimento diz, quando diz um só. */
export function diaDoTexto(t: string): { dia: number | null; data: string | null } {
  const s = t.trim();
  const valido = (n: number) => (n >= 1 && n <= 31 ? n : null);
  // "5", "Dia 5", "dia 05"
  const so = s.match(/^(?:dia\s*)?(\d{1,2})$/i);
  if (so) return { dia: valido(Number(so[1])), data: null };
  // Uma data inteira no texto ("20/01/2027", "próxima renovação 02/10/27"),
  // desde que o resto não fale de outro dia ("antes 29 - confirmar ...").
  const d = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (d && !/\d/.test(s.replace(d[0], ""))) return { dia: valido(Number(d[1])), data: dataNoTexto(d[0]) };
  // "15-jan."
  const diaMes = s.match(/^(\d{1,2})\s*[-/]\s*[a-z]/i);
  if (diaMes) return { dia: valido(Number(diaMes[1])), data: null };
  // "12/31", "11/19": mês/dia, quando um dos dois não pode ser mês
  const dois = s.match(/^(\d{1,2})\/(\d{1,2})$/);
  if (dois) {
    const [a, b] = [Number(dois[1]), Number(dois[2])];
    if (b > 12 && a <= 12) return { dia: valido(b), data: null };
    if (a > 12 && b <= 12) return { dia: valido(a), data: null };
  }
  return { dia: null, data: null };
}

export function lerRenovacao(t: string): FerramentaPlanilha["renovacao"] {
  const n = normalizar(t);
  if (n === "s" || n === "sim") return "automatica";
  if (n === "n" || n === "nao") return "manual";
  return null;
}

/** "ADOBE / EBN ADOBE" → ["ADOBE"]; o que o Conta Azul e o cartão escrevem. */
export function termosDe(t: string): string[] {
  return [...new Set(t.split("/")
    .map((p) => p.toUpperCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/\bEBN\b\s*\*?/g, " ").replace(/[^A-Z0-9 ]/g, " ").replace(/\s+/g, " ").trim())
    .filter((p) => p.length >= 3))];
}

/**
 * O que o Nexa calcula a partir dos textos da planilha. A tela chama ao
 * salvar, a importação ao ler: as duas chegam no mesmo resultado.
 */
export function derivarCampos(f: {
  dia_vencimento: string | null;
  nome_extrato: string | null;
  usuarios_ativos: string | null;
  periodicidade: string | null;
}): { dia_cobranca: number | null; proxima_renovacao: string | null; termos_busca: string[]; qtd_usuarios: number | null } {
  const dia = f.dia_vencimento ? diaDoTexto(f.dia_vencimento) : { dia: null, data: null };
  const usuarios = (f.usuarios_ativos ?? "").trim();
  const anual = /anual|anuidade/.test(normalizar(f.periodicidade ?? ""));
  return {
    dia_cobranca: dia.dia,
    proxima_renovacao: dia.data && anual ? dia.data : null,
    termos_busca: termosDe(f.nome_extrato ?? ""),
    qtd_usuarios: /^\d+$/.test(usuarios) ? Number(usuarios) : null,
  };
}

// ── Controle Geral e Cancelados ────────────────────────────────────────────

export function interpretarAba(dados: SheetData | undefined, aba: FerramentaPlanilha["aba"], comAcesso = false): {
  ferramentas: FerramentaPlanilha[];
  acessos: AcessoPlanilha[];
  ignoradas: string[];
  erro?: string;
} {
  const vazio = { ferramentas: [], acessos: [], ignoradas: [] };
  if (!dados) return { ...vazio, erro: `A aba ${aba === "controle" ? "Controle Geral" : "Cancelados"} não foi encontrada.` };
  // A linha das colunas: a primeira que tem "Ferramenta" e "Status". Acima dela
  // há uma linha de grupos (IDENTIFICAÇÃO, FINANCEIRO, ...).
  const iCab = dados.findIndex((r) => r.some((c) => normalizar(texto(c)).startsWith("ferramenta"))
    && r.some((c) => normalizar(texto(c)) === "status"));
  if (iCab < 0) return { ...vazio, erro: "Não achei a linha de títulos (com Ferramenta e Status)." };
  const { indice: ix, ignoradas } = mapearColunas(dados[iCab]!, comAcesso);
  const cel = (r: Celula[], k: Chave) => (ix[k] === undefined ? undefined : r[ix[k]!]);
  const txt = (r: Celula[], k: Chave) => texto(cel(r, k));

  const vistos = new Map<string, number>();
  const ferramentas: FerramentaPlanilha[] = [];
  const acessos: AcessoPlanilha[] = [];
  dados.slice(iCab + 1).forEach((r, k) => {
    const nome = txt(r, "nome");
    if (!nome) return;
    const avisos: string[] = [];
    const textoPlanilha: FerramentaPlanilha["texto_planilha"] = {};

    const statusTxt = txt(r, "status");
    const st = lerStatus(statusTxt);
    if (!st.lido) {
      if (statusTxt) textoPlanilha.status = statusTxt;
      avisos.push(statusTxt ? `status "${statusTxt}" não reconhecido` : "sem status: ficou \"Verificar\"");
    }
    // Na aba Cancelados, tudo é cancelada — o texto diferente fica ao lado.
    const status = aba === "cancelados" ? "cancelada" : st.status;
    if (aba === "cancelados" && st.lido && st.status !== "cancelada") textoPlanilha.status = statusTxt;

    const moedaTxt = normalizar(txt(r, "moeda"));
    const moeda: "BRL" | "USD" = moedaTxt.includes("usd") || moedaTxt === "us$" ? "USD" : "BRL";
    if (!moedaTxt) avisos.push("sem moeda: ficou BRL");

    const valor = (k: Chave, coluna: ColunaComTexto) => {
      const v = lerValorCelula(cel(r, k));
      if (v.texto) textoPlanilha[coluna] = v.texto;
      return v.centavos;
    };
    const valores = {
      valor_mensal_centavos: valor("mensal", "valor_mensal_centavos"),
      valor_trimestral_centavos: valor("trimestral", "valor_trimestral_centavos"),
      valor_semestral_centavos: valor("semestral", "valor_semestral_centavos"),
      valor_anual_centavos: valor("anual", "valor_anual_centavos"),
      valor_total_ano_centavos: valor("total", "valor_total_ano_centavos"),
      valor_usd_centavos: valor("usd", "valor_usd_centavos"),
    };
    if (Object.values(valores).every((v) => !v) && !Object.keys(textoPlanilha).some((c) => c.startsWith("valor_"))) {
      avisos.push("sem valor");
    }

    const formaTxt = txt(r, "forma");
    const forma = lerFormaPagamento(formaTxt);
    if (forma.forma === "outro") textoPlanilha.forma_pagamento = formaTxt;
    const dia = lerDia(cel(r, "dia"));
    const renovTxt = txt(r, "renovacao");
    const renovacao = lerRenovacao(renovTxt);
    if (renovTxt && !renovacao) textoPlanilha.renovacao = renovTxt;

    const extrato = txt(r, "extrato");
    const usuarios = txt(r, "usuarios");
    const periodicidade = ou(txt(r, "periodicidade"));
    // Versão final de 22/10: o final do cartão às vezes vem na periodicidade
    // ("Mensal - Final 3561"). Vai para o cartão, se a forma é cartão e o final
    // não veio na coluna dele; o texto fica como está (a 074 o lê como mensal).
    const finalNaPeriodicidade = finalDoCartaoNaPeriodicidade(periodicidade);
    if (forma.forma === "cartao" && !forma.final && finalNaPeriodicidade) {
      forma.final = finalNaPeriodicidade;
      avisos.push("o final do cartão estava na periodicidade");
    }
    const derivados = derivarCampos({ dia_vencimento: dia.texto, nome_extrato: extrato, usuarios_ativos: usuarios, periodicidade });
    if (derivados.termos_busca.length === 0) avisos.push("sem o nome no cartão / extrato: a cobrança não será reconhecida sozinha");

    // A referência de origem: aba + nome + descritivo. É o que torna a
    // importação idempotente; repetida na mesma aba, ganha um número.
    let ref = `${aba}:${normalizar(nome)}|${normalizar(extrato)}`;
    const n = (vistos.get(ref) ?? 0) + 1;
    vistos.set(ref, n);
    if (n > 1) ref = `${ref}#${n}`;

    // Na aba Cancelados da planilha real, parte das linhas tem o centro de custo
    // embaixo do título "Conta Contábil" e a conta embaixo de "Centro de Custo".
    // Quando o "centro de custo" é um código de conta e a "conta" não é, destroca.
    let ccTexto = txt(r, "cc"), contaTexto = txt(r, "conta");
    const ehCodigo = (s: string) => /^\s*\d+(\.\d+)+/.test(s);
    if (ehCodigo(ccTexto) && !ehCodigo(contaTexto)) {
      [ccTexto, contaTexto] = [contaTexto, ccTexto];
      avisos.push("centro de custo e conta estavam trocados na planilha");
    }

    ferramentas.push({
      aba, linha_planilha: iCab + 2 + k, origem_ref: ref, nome, tipo: ou(txt(r, "tipo")),
      status, cancelada_em: status === "cancelada" ? st.cancelada_em : null,
      moeda, ...valores, periodicidade, tipo_cobranca: ou(txt(r, "cobranca")),
      forma_pagamento: forma.forma, cartao_final: forma.forma === "cartao" ? forma.final : null,
      dia_vencimento: dia.texto, dia_cobranca: dia.dia, proxima_renovacao: derivados.proxima_renovacao,
      renovacao, centro_custo_texto: ccTexto, conta_texto: contaTexto,
      nome_extrato: ou(extrato), termos_busca: derivados.termos_busca,
      responsavel: ou(txt(r, "responsavel")), usuarios_ativos: ou(usuarios), qtd_usuarios: derivados.qtd_usuarios,
      uso: ou(txt(r, "uso")), url_acesso: ou(txt(r, "url")), observacao: ou(txt(r, "observacoes")), tendencia: null,
      texto_planilha: textoPlanilha, avisos_importacao: avisos,
    });
    if (comAcesso) {
      const login = ou(txt(r, "login")), senha = ou(txt(r, "senha"));
      if (login || senha) acessos.push({ origem_ref: ref, login, senha });
    }
  });
  return { ferramentas, acessos, ignoradas };
}

// ── Pendências, Novas dos CSVs, Painel Financeiro, Instruções ──────────────

/** A linha de títulos de uma aba: a primeira com todos os trechos pedidos. */
function linhaDeTitulos(dados: SheetData, trechos: string[]): number {
  return dados.findIndex((r) => trechos.every((t) => r.some((c) => normalizar(texto(c)).includes(t))));
}

function colunasPorTitulo(cab: Celula[], mapa: Record<string, (t: string) => boolean>): Record<string, number | undefined> {
  const ix: Record<string, number | undefined> = {};
  cab.forEach((c, i) => {
    const t = normalizar(texto(c));
    for (const [k, casa] of Object.entries(mapa)) if (ix[k] === undefined && t && casa(t)) { ix[k] = i; break; }
  });
  return ix;
}

/** "🔴 CRÍTICO" → "critico". */
export function lerUrgencia(t: string): PendenciaPlanilha["urgencia"] {
  const n = normalizar(t);
  if (n.includes("crit")) return "critico";
  if (n.includes("urgente")) return "urgente";
  if (n.includes("medi")) return "medio";
  if (n.includes("baix")) return "baixo";
  return null;
}

const refUnica = (vistos: Map<string, number>, ref: string) => {
  const n = (vistos.get(ref) ?? 0) + 1;
  vistos.set(ref, n);
  return n > 1 ? `${ref}#${n}` : ref;
};

export function interpretarPendencias(dados: SheetData | undefined): PendenciaPlanilha[] {
  if (!dados) return [];
  const iCab = linhaDeTitulos(dados, ["ferramenta", "urgencia"]);
  if (iCab < 0) return [];
  const ix = colunasPorTitulo(dados[iCab]!, {
    ferramenta: (t) => t.startsWith("ferramenta"), urgencia: (t) => t.startsWith("urgencia"),
    motivo: (t) => t.startsWith("motivo"), acao: (t) => t.startsWith("acao"), prazo: (t) => t.startsWith("prazo"),
    responsavel: (t) => t.startsWith("responsavel"), resolvido: (t) => t.startsWith("resolvido"),
  });
  const vistos = new Map<string, number>();
  const at = (r: Celula[], k: string) => texto(ix[k] === undefined ? undefined : r[ix[k]!]);
  const saida: PendenciaPlanilha[] = [];
  dados.slice(iCab + 1).forEach((r, k) => {
    const ferramenta = at(r, "ferramenta");
    if (!ferramenta) return;
    const motivo = at(r, "motivo");
    saida.push({
      origem_ref: refUnica(vistos, `pendencias:${normalizar(ferramenta)}|${normalizar(motivo).slice(0, 80)}`),
      linha_planilha: iCab + 2 + k, secao: "pendencias", ferramenta_texto: ferramenta,
      urgencia: lerUrgencia(at(r, "urgencia")), motivo: ou(motivo), acao_sugerida: ou(at(r, "acao")),
      prazo: ou(at(r, "prazo")), responsavel: ou(at(r, "responsavel")),
      resolvido: /^(s|sim|x|ok|true|resolvido|✓|✔)$/i.test(at(r, "resolvido")),
    });
  });
  return saida;
}

/** O bloco "Atenções críticas" da aba Instruções: ferramenta e o texto. */
export function interpretarAtencoes(dados: SheetData | undefined): PendenciaPlanilha[] {
  if (!dados) return [];
  const iBloco = dados.findIndex((r) => normalizar(texto(r[0])).includes("atencoes criticas"));
  if (iBloco < 0) return [];
  const vistos = new Map<string, number>();
  const saida: PendenciaPlanilha[] = [];
  for (let i = iBloco + 1; i < dados.length; i++) {
    const r = dados[i]!;
    const ferramenta = texto(r[0]);
    const nota = r.slice(1).map(texto).filter(Boolean).join(" ");
    if (!ferramenta && !nota) break;
    if (!ferramenta) continue;
    saida.push({
      origem_ref: refUnica(vistos, `atencoes:${normalizar(ferramenta)}`), linha_planilha: i + 1,
      secao: "atencoes_criticas", ferramenta_texto: ferramenta, urgencia: null, motivo: ou(nota),
      acao_sugerida: null, prazo: null, responsavel: null, resolvido: false,
    });
  }
  return saida;
}

export function interpretarNovas(dados: SheetData | undefined): CandidataPlanilha[] {
  if (!dados) return [];
  const iCab = linhaDeTitulos(dados, ["ferramenta", "descritivo"]);
  if (iCab < 0) return [];
  const ix = colunasPorTitulo(dados[iCab]!, {
    ferramenta: (t) => t.startsWith("ferramenta"), descritivo: (t) => t.startsWith("descritivo"),
    faturas: (t) => t.startsWith("faturas"), valores: (t) => t.startsWith("valores"), dia: (t) => t.startsWith("dia"),
    classificacao: (t) => t.startsWith("classificacao"), status: (t) => t.startsWith("status"), acao: (t) => t.startsWith("acao"),
  });
  const vistos = new Map<string, number>();
  const at = (r: Celula[], k: string) => texto(ix[k] === undefined ? undefined : r[ix[k]!]);
  const saida: CandidataPlanilha[] = [];
  dados.slice(iCab + 1).forEach((r, k) => {
    const ferramenta = at(r, "ferramenta");
    if (!ferramenta) return;
    saida.push({
      origem_ref: refUnica(vistos, `novas:${normalizar(ferramenta)}|${normalizar(at(r, "descritivo"))}`),
      linha_planilha: iCab + 2 + k, ferramenta_texto: ferramenta, descritivo_extrato: ou(at(r, "descritivo")),
      faturas: ou(at(r, "faturas")), valores_observados: ou(at(r, "valores")), dia_cartao: ou(at(r, "dia")),
      classificacao: ou(at(r, "classificacao")), status_texto: ou(at(r, "status")), acao: ou(at(r, "acao")),
    });
  });
  return saida;
}

/**
 * A "Obs / Tendência" do Painel Financeiro, pelo nome da ferramenta. O painel
 * tem linhas que são parte de uma ferramenta ("Canva — R$35", "Memberkit
 * (3561)"), e essas não casam com nome nenhum do cadastro: contadas à parte.
 */
export function interpretarPainel(dados: SheetData | undefined): { tendencias: Map<string, string>; linhas: number } {
  const tendencias = new Map<string, string>();
  if (!dados) return { tendencias, linhas: 0 };
  const iCab = linhaDeTitulos(dados, ["ferramenta", "tendencia"]);
  if (iCab < 0) return { tendencias, linhas: 0 };
  const cab = dados[iCab]!;
  const iNome = cab.findIndex((c) => normalizar(texto(c)).startsWith("ferramenta"));
  const iObs = cab.findIndex((c) => normalizar(texto(c)).includes("tendencia"));
  let linhas = 0;
  for (const r of dados.slice(iCab + 1)) {
    const nome = texto(r[iNome]);
    if (!nome) continue;
    linhas++;
    // A coluna sem título logo depois ("Adicionada Recorrência CA - ...") vai junto.
    const obs = [texto(r[iObs]), texto(r[iObs + 1])].filter(Boolean).join(" — ");
    if (!obs) continue;
    const k = normalizar(nome);
    tendencias.set(k, tendencias.has(k) ? `${tendencias.get(k)}; ${obs}` : obs);
  }
  return { tendencias, linhas };
}

// ── casar com o cadastro do Nexa ───────────────────────────────────────────

const PALAVRAS_VAZIAS = new Set(["e", "de", "do", "da", "dos", "das", "a", "o"]);
const tokens = (s: string) => normalizar(s).replace(/[^a-z0-9 ]/g, " ").split(" ").filter((t) => t && !PALAVRAS_VAZIAS.has(t));
const parecem = (a: string, b: string) => a === b || (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a)));

/** Sobreposição pelos termos do menor: "Produtos" casa com "Produtos e Ensino (Entrega)". */
export function semelhanca(a: string, b: string): number {
  const ta = tokens(a), tb = tokens(b);
  if (!ta.length || !tb.length) return 0;
  const comuns = ta.filter((x) => tb.some((y) => parecem(x, y))).length;
  return comuns / Math.min(ta.length, tb.length);
}

export function sugerirCentro<T extends { id: string; codigo: string; nome: string }>(t: string, centros: T[]): T | null {
  if (!t.trim()) return null;
  const porCodigo = centros.find((c) => c.codigo === t.trim());
  if (porCodigo) return porCodigo;
  let melhor: T | null = null, nota = 0;
  for (const c of centros) {
    const s = semelhanca(t, c.nome);
    if (s > nota) { nota = s; melhor = c; }
  }
  return nota >= 0.5 ? melhor : null;
}

/**
 * Pelo código; sem código, pelo nome. ⚠️ Com código E nome, os dois têm de
 * concordar: na planilha real, "2.03.03.05 - Treinamentos e Cursos" aponta para
 * uma conta que na produção é outra ("Auxílio bem-estar"). Casar só pelo código
 * classificaria errado sem erro nenhum — o mesmo caso do orçamento em 17/09 —,
 * então a divergência não casa e fica para alguém escolher.
 */
export function sugerirCategoria<T extends { id: string; codigo: string | null; nome: string }>(t: string, categorias: T[]): T | null {
  const codigo = (t.match(/^\s*(\d+(\.\d+)+)/) ?? [])[1];
  if (codigo) {
    const porCodigo = categorias.find((c) => c.codigo === codigo) ?? null;
    const nome = t.trim().slice(codigo.length).replace(/^[\s\-–—:]+/, "");
    if (porCodigo && nome && semelhanca(nome, porCodigo.nome) < 0.5) return null;
    return porCodigo;
  }
  if (!t.trim()) return null;
  let melhor: T | null = null, nota = 0;
  for (const c of categorias) {
    const s = semelhanca(t, c.nome);
    if (s > nota) { nota = s; melhor = c; }
  }
  return nota >= 0.75 ? melhor : null;
}

// ── o arquivo ──────────────────────────────────────────────────────────────

export type LeituraFerramentas = {
  nomeArquivo: string;
  ferramentas: FerramentaPlanilha[];
  /** Só com `comAcesso`: login e senha por `origem_ref`. */
  acessos: AcessoPlanilha[];
  pendencias: PendenciaPlanilha[];
  candidatas: CandidataPlanilha[];
  /** Linhas do Painel Financeiro, e quantas ferramentas ganharam a "Obs / Tendência". */
  painel: { linhas: number; casadas: number };
  /** Colunas que o leitor deixou de fora de propósito (login, senha). */
  ignoradas: string[];
  erros: string[];
};

/** As abas já lidas (pelo navegador ou pela linha de comando) → o que importar. */
export function interpretarArquivo(nomeArquivo: string, abas: { sheet: string; data: SheetData }[], comAcesso = false): LeituraFerramentas {
  const acha = (trecho: string) => abas.find((a) => normalizar(a.sheet).includes(trecho))?.data;
  const controle = interpretarAba(acha("controle geral"), "controle", comAcesso);
  const cancelados = acha("cancelados");
  const canc = cancelados ? interpretarAba(cancelados, "cancelados", comAcesso) : { ferramentas: [], acessos: [], ignoradas: [] as string[] };
  const ferramentas = [...controle.ferramentas, ...canc.ferramentas];

  const painel = interpretarPainel(acha("painel"));
  let casadas = 0;
  for (const f of ferramentas) {
    const t = painel.tendencias.get(normalizar(f.nome));
    if (t) { f.tendencia = t; casadas++; }
  }
  return {
    nomeArquivo,
    ferramentas,
    acessos: [...controle.acessos, ...canc.acessos],
    pendencias: [...interpretarPendencias(acha("pendencias")), ...interpretarAtencoes(acha("instruc"))],
    candidatas: interpretarNovas(acha("novas")),
    painel: { linhas: painel.linhas, casadas },
    ignoradas: [...new Set([...controle.ignoradas, ...canc.ignoradas])],
    erros: [controle.erro, "erro" in canc ? canc.erro : undefined].filter((x): x is string => !!x),
  };
}

export async function lerFerramentasXlsx(arquivo: File, comAcesso = false): Promise<LeituraFerramentas> {
  const { default: readExcelFile } = await import("read-excel-file/browser");
  const abas = await readExcelFile(arquivo) as { sheet: string; data: SheetData }[];
  return interpretarArquivo(arquivo.name, abas, comAcesso);
}
